import { useCallback, useEffect, useRef, useState } from 'react';
import type { ApiResult } from '../auth/api-client';

export interface DashboardCache<T> {
  result?: ApiResult<T>;
  attemptedAt?: number;
  pending?: Promise<ApiResult<T>>;
}

export async function refreshDashboard<T>(
  cache: DashboardCache<T>,
  load: () => Promise<ApiResult<T>>,
  interval: number,
  force = false,
): Promise<ApiResult<T>> {
  if (cache.pending) return cache.pending;
  if (!force && cache.result && Date.now() - (cache.attemptedAt ?? 0) < interval)
    return cache.result;
  cache.attemptedAt = Date.now();
  cache.pending = Promise.resolve()
    .then(load)
    .catch(
      (): ApiResult<T> => ({
        ok: false,
        status: 0,
        error: 'network_error',
        message: 'Unable to refresh dashboard.',
      }),
    )
    .then((result) => {
      cache.result = result;
      return result;
    })
    .finally(() => {
      cache.pending = undefined;
    });
  return cache.pending;
}

/** The rotating parent owns the cache, not the mounted chart, or any global user state. */
export function useDashboardRefresh<T>(
  load: () => Promise<ApiResult<T>>,
  interval: number,
  sharedCache?: DashboardCache<T>,
) {
  const local = useRef<DashboardCache<T>>({});
  const cache = sharedCache ?? local.current;
  const [result, setResult] = useState(cache.result);
  const [refreshing, setRefreshing] = useState(false);
  const mounted = useRef(false);
  const refresh = useCallback(
    async (force = false, override = load) => {
      if (document.visibilityState === 'hidden') return;
      setRefreshing(true);
      try {
        const next = await refreshDashboard(cache, override, interval, force);
        if (mounted.current) setResult(next);
      } finally {
        if (mounted.current) setRefreshing(false);
      }
    },
    [cache, interval, load],
  );
  useEffect(() => {
    mounted.current = true;
    void refresh();
    const timer = window.setInterval(() => void refresh(), interval);
    const visible = () => {
      void refresh();
    };
    document.addEventListener('visibilitychange', visible);
    return () => {
      mounted.current = false;
      window.clearInterval(timer);
      document.removeEventListener('visibilitychange', visible);
    };
  }, [refresh, interval]);
  return { result, refreshing, refresh };
}
